/** A bounded session outbox for genuine, consented download clicks. */
export interface DownloadConversion {
  event_id: string;
  event_time: number;
  event_source_url: string;
  marketing_consent: 'granted';
  fbc?: string;
  fbp?: string;
}
export const DOWNLOAD_QUEUE_KEY = 'vovy_dl_capi_pending_v1';
const MAX_QUEUE = 10;
const TTL_SECONDS = 7 * 24 * 60 * 60;
export function downloadSourceUrl(href: string): string | null {
  try {
    const url = new URL(href);
    if (url.protocol !== 'https:' || !['vovy.ai', 'www.vovy.ai'].includes(url.hostname) || url.port || url.username || url.password) return null;
    return url.origin + url.pathname;
  } catch { return null; }
}
interface Dependencies {
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  consent: () => boolean;
  now: () => number;
  send: (event: DownloadConversion) => Promise<boolean>;
}
export function createDownloadConversionQueue(deps: Dependencies) {
  let inFlight: Promise<void> | undefined;
  const save = (events: DownloadConversion[]): void => {
    if (events.length) deps.storage.setItem(DOWNLOAD_QUEUE_KEY, JSON.stringify(events.slice(-MAX_QUEUE)));
    else deps.storage.removeItem(DOWNLOAD_QUEUE_KEY);
  };
  const read = (): DownloadConversion[] => {
    const raw = deps.storage.getItem(DOWNLOAD_QUEUE_KEY);
    let decoded: unknown;
    try { decoded = raw && raw.length <= 32_000 ? JSON.parse(raw) : []; } catch { return []; }
    if (!Array.isArray(decoded)) return [];
    const now = Math.floor(deps.now() / 1000);
    const unique = new Map<string, DownloadConversion>();
    for (const entry of decoded) {
      if (!entry || typeof entry !== 'object' || typeof entry.event_id !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(entry.event_id) ||
          !Number.isInteger(entry.event_time) || entry.event_time > now || entry.event_time <= now - TTL_SECONDS ||
          entry.marketing_consent !== 'granted' || typeof entry.event_source_url !== 'string' || downloadSourceUrl(entry.event_source_url) !== entry.event_source_url) continue;
      unique.set(entry.event_id, {
        event_id: entry.event_id, event_time: entry.event_time, event_source_url: entry.event_source_url, marketing_consent: 'granted',
        ...(typeof entry.fbc === 'string' && entry.fbc.length <= 512 ? { fbc: entry.fbc } : {}),
        ...(typeof entry.fbp === 'string' && entry.fbp.length <= 512 ? { fbp: entry.fbp } : {})
      });
    }
    return [...unique.values()].slice(-MAX_QUEUE);
  };
  const clear = (): void => { try { save([]); } catch { /* Storage may be unavailable. */ } };
  const flush = (): Promise<void> => {
    if (!deps.consent()) { clear(); return Promise.resolve(); }
    if (inFlight) return inFlight;
    inFlight = (async () => {
      try {
        const attempted = new Set<string>();
        while (deps.consent()) {
          const current = read(); save(current);
          const next = current.find(event => !attempted.has(event.event_id));
          if (!next) return;
          attempted.add(next.event_id);
          let accepted = false;
          try { accepted = await deps.send(next); } catch { /* Retain for next installing/online retry. */ }
          if (!deps.consent()) { clear(); return; }
          if (accepted) {
            save(read().filter(event => event.event_id !== next.event_id));
            deps.storage.setItem('vovy_dl_capi_sent', next.event_id);
          }
        }
        clear();
      } catch { /* Conversion reporting must never block navigation. */ }
    })().finally(() => { inFlight = undefined; });
    return inFlight;
  };
  const enqueue = (event: DownloadConversion): void => {
    if (!deps.consent()) { clear(); return; }
    try {
      // Never reconstruct old vovy_dl_event_id values: they have no genuine click time.
      const current = read();
      if (!current.some(item => item.event_id === event.event_id) && deps.storage.getItem('vovy_dl_capi_sent') !== event.event_id) save([...current, event]);
    } catch { /* No durable outbox available; preserve the download itself. */ }
  };
  return { enqueue, flush, clear };
}
